import { localTime, ui } from '@beelday/common'
import { QuizId, UUID } from 'common/types'

type GroupingType = 'MANUAL' | 'AUTOMATIC'

export type GroupingActivity = {
	type: 'GROUPING'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: UUID
			name: string
			description: string
			groupSize: number
			groupingType: GroupingType
			doneInPreviousSession?: boolean
		}
	}
}

/**
 * A stationary interaction scheme rewrites every `GROUPING` activity into this
 * one on the way in (`TrainingIsFactory`'s
 * `GroupingToSimpleGroupingScriptVisitor`), so it only ever appears in an
 * in-person session.
 *
 * `SimpleGroupingDefinition` is a record on the backend, so the task it was
 * configured with rides along in `SCRIPT_STATE_CHANGED` exactly as every other
 * activity's configuration does. The screens do not read it from here - see
 * `useSimpleGroupingTask`.
 */
export type SimpleGroupingActivity = {
	type: 'SIMPLE_GROUPING'
	needsConfirmation: boolean
	state: {
		configuration: {
			name?: string
			description?: string
		}
	}
}

type KnowledgeCheckQuestionType = 'SINGLE_SELECT' | 'MULTI_SELECT'
export type KnowledgeCheckActivity = {
	type: 'KNOWLEDGE_CHECK'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: UUID
			duration?: number
			name: string
			questions: {
				image?: string
				question: string
				options: {
					answer: string
					correct: boolean
				}[]
				type: KnowledgeCheckQuestionType
			}[]
			groupingType: GroupingType
			doneInPreviousSession?: boolean
		}
	}
}

export type QuizActivity = {
	type: 'QUIZ'
	quizId?: QuizId
	needsConfirmation: boolean
}

export type BreakActivity = {
	type: 'BREAK'
	needsConfirmation: boolean
}

export type AudioStreamingActivity = {
	type: 'AUDIO_STREAMING'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: UUID
			name: string
			description: string
			creatorId: UUID
			fileName: string
			originalFilename: string
		}
	}
}

export type YouTubeStreamingActivity = {
	type: 'YOUTUBE'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: UUID
			name: string
			description: string
			creatorId: UUID
			link: string
		}
	}
}
export type Activity =
	| QuizActivity
	| BreakActivity
	| GroupingActivity
	| SimpleGroupingActivity
	| KnowledgeCheckActivity
	| AudioStreamingActivity
	| YouTubeStreamingActivity

// Only GROUPING and KNOWLEDGE_CHECK activities ever carry this field from the
// backend (confirmed via com.beelday.core.features.{grouping,knowledgecheck}
// domain definitions) - every other activity type is never "done in a
// previous session" and always returns false.
export const isDoneInPreviousSession = (activity: Activity): boolean => {
	switch (activity.type) {
		case 'GROUPING':
		case 'KNOWLEDGE_CHECK':
			return !!activity.state.configuration.doneInPreviousSession
		default:
			return false
	}
}

type ActivityIconType = Parameters<typeof ui.ActivityIcon>[0]['type']

/**
 * `ui.ActivityIcon` predates in-person and has no `SIMPLE_GROUPING` art, so it
 * would fall through to a blank tile. A stationary interaction scheme only
 * rewrites a `GROUPING` activity into that type - it is the same thing to
 * whoever reads the script, and the copy
 * (`training.script.tool.SIMPLE_GROUPING.*`) already matches - so it borrows
 * the same icon. Mapped here rather than in `common` so the shared
 * component's contract stays untouched.
 */
export const activityIconType = (activity: Activity): ActivityIconType =>
	activity.type === 'SIMPLE_GROUPING' ? 'GROUPING' : activity.type

export type ScriptItem = {
	title: string
	description: string
	startTime: Date
	activities: Activity[]
}

export type SessionChunk = {
	startDate: Date
	startTime: localTime.LocalTime
	startTitle?: string
	startDescription?: string
	endTime: localTime.LocalTime
	endTitle?: string
	endDescription?: string
	items: ScriptItem[]
}
